<script setup>
/* Imports */
import { computed, onBeforeUnmount } from 'vue';
import { MediaGallery } from '@garmin/ui-product-media-gallery';
import { PcRenderPromo } from '@garmin/components-product';
import GallerySkeletonLoader from '../components/GallerySkeletonLoader.vue';
import { mapGetters, breakpoints } from '../helpers/mainHelpers.js';
import { buildCustomerMediaImages, buildProductMediaImages, buildProductVideos } from '../helpers/mediaGallery.js';
import { debounce } from '../helpers/debounce.js';
import { useScreenResize } from '../composables/useScreenResize.js';

import {
  sendAnalyticsEvent,
  sanitizeAnalyticsString,
  getAnalyticsPriceData,
} from '../helpers/ga/analytics.js';

/* Composables */
import { useGenericAnalyticsDataHelper } from '../composables/useAnalyticsData.js';

/* Props */
const props = defineProps({
  showDesktopBanner: {
    type: Boolean,
    default: false,
  },
  currencyCode: {
    type: Array,
    default: () => [],
  },
  productId: {
    type: String,
    default: '',
  },
  partNumber: {
    type: String,
    default: '',
  },
  productName: {
    type: String,
    default: '',
  },
  priceData: {
    type: Object,
    default: () => ({}),
  },
  productVariation: {
    type: String,
    default: '',
  },
  promoBanner: {
    type: String,
    default: '',
  },
  cloudinaryAssets: {
    type: Array,
    default: () => [],
  },
  customerImages: {
    type: Array,
    default: () => [],
  },
  videosUrls: {
    type: String,
    default: '',
  },
});

const { genericAnalyticsData } = useGenericAnalyticsDataHelper();

/* Store - Getters */
const {
  getGalleryLoaded: galleryLoaded,
  getTranslations: translations,
  getProductBySku: productBySku,
} = mapGetters();

/* Debounce slide-change so only the last slide viewed for 2s is reported */
const SLIDE_VIEW_DELAY_MS = 2000;

const { screenWidth } = useScreenResize();

const isMobileView = computed(() => screenWidth.value <= breakpoints['mobile-s']);

const ANALYTICS_MEDIA_TYPE = {
  productImages: 'Product Image',
  productVideos: 'Product Video',
  customerImages: 'Customer Image',
};

const mediaGalleryTranslations = computed(() => translations.value?.ITFE_PRODUCT_PAGES_MEDIA_GALLERY_TRANSLATIONS);
const computedMediaGalleryTranslations = computed(() => ({
  tabs: {
    productImages: mediaGalleryTranslations.value?.ITFE_PRODUCT_PAGES_MEDIA_GALLERY_TAB_PRODUCT_IMAGES ?? 'Product Images',
    productVideos: mediaGalleryTranslations.value?.ITFE_PRODUCT_PAGES_MEDIA_GALLERY_TAB_VIDEOS ?? 'Videos',
    customerImages: mediaGalleryTranslations.value?.ITFE_PRODUCT_PAGES_MEDIA_GALLERY_TAB_CUSTOMER_IMAGES ?? 'Customer Images',
  },
  customerImagesText: mediaGalleryTranslations.value?.ITFE_PRODUCT_PAGES_MEDIA_GALLERY_CUSTOMER_IMAGES_TEXT ?? 'Tag #Garmin for a chance to be featured.',
  noMediaText: mediaGalleryTranslations.value?.ITFE_PRODUCT_PAGES_MEDIA_GALLERY_NO_MEDIA_AVAILABLE ?? 'No media available',
}));

const productImages = computed(() => buildProductMediaImages(
  props.cloudinaryAssets,
  props.productName,
  props.partNumber,
));

const computedCustomerImages = computed(() => buildCustomerMediaImages(
  props.customerImages,
  props.productName,
));

const productVideos = computed(() => buildProductVideos(props.videosUrls));

const shouldRenderPromoBanner = computed(() => props.showDesktopBanner && Boolean(props.promoBanner));

const productPrice = computed(() => {
  const productData = productBySku.value(props.partNumber);
  return {
    priceObj: props.priceData,
    sellable: productData?.isSellable,
    showPrice: productData?.showPrice,
  };
});

/* Methods */
const sendViewerOpenEvent = (tab) => {
  const analyticsPriceData = getAnalyticsPriceData(productPrice.value);
  const analyticsData = {
    ...genericAnalyticsData(),
    tealium_event: 'viewer_open',
    currency_code: props.currencyCode,
    media_type: ANALYTICS_MEDIA_TYPE[tab] ?? '',
    index: 'NA',
    is_sale: analyticsPriceData?.isSale,
    location_id: 'Product Page',
    price_shown: analyticsPriceData?.priceShown,
    product_id: props.productId,
    product_name: sanitizeAnalyticsString(props.productName),
    product_original_price: analyticsPriceData.listPrice,
    product_price: props.priceData?.salePrice?.price ?? props.priceData?.listPrice?.price ?? '',
    product_sku: props.partNumber,
    product_url: window?.location?.href?.split('?')?.[0] || 'NA',
    product_variant: sanitizeAnalyticsString(props.productVariation),
  };

  sendAnalyticsEvent(analyticsData);
};

const sendMediaImpressionEvent = (payload) => {
  const analyticsPriceData = getAnalyticsPriceData(productPrice.value);
  const analyticsData = {
    ...genericAnalyticsData(),
    tealium_event: 'media_impression',
    media_type: payload?.mediaType || 'NA',
    index: Number(payload?.index) + 1 || 'NA',
    product_name: sanitizeAnalyticsString(props.productName) || 'NA',
    location_id: payload?.locationId || 'Product Page',
    product_id: props.productId || 'NA',
    product_original_price: analyticsPriceData?.listPrice,
    product_price: analyticsPriceData?.price,
    price_shown: analyticsPriceData?.priceShown,
    is_sale: analyticsPriceData?.isSale,
    product_sku: props.partNumber || 'NA',
    product_variant: sanitizeAnalyticsString(props.productVariation) || 'NA',
    product_url: window?.location?.href ? [window.location.href.split('?')[0]] : ['NA'],
  };

  sendAnalyticsEvent(analyticsData);
};

// Debounce the media impression event sender to avoid excessive analytics calls on rapid slide changes.
const debouncedSendMediaImpressionEvent = debounce(sendMediaImpressionEvent, SLIDE_VIEW_DELAY_MS);

const trackVideoAnalytics = ({ interaction, payload }) => {
  const analyticsPriceData = getAnalyticsPriceData(productPrice.value);
  const analyticsData = {
    ...genericAnalyticsData(),
    tealium_event: interaction,
    currency_code: props.currencyCode,
    video_current_time: payload?.video_current_time,
    video_duration: payload?.video_duration,
    video_percent: payload?.video_percent,
    video_provider: payload?.video_provider,
    video_title: payload?.video_title,
    video_url: payload?.video_url,
    index: Number(payload?.index) + 1 || 'NA',
    is_sale: analyticsPriceData?.isSale,
    gallery_type: 'Page Gallery',
    price_shown: analyticsPriceData?.priceShown,
    product_id: props.productId,
    product_name: sanitizeAnalyticsString(props.productName),
    product_original_price: analyticsPriceData?.listPrice,
    product_price: analyticsPriceData?.price,
    product_sku: props.partNumber,
    product_url: window?.location?.href?.split('?')?.[0] || 'NA',
    product_variant: sanitizeAnalyticsString(props.productVariation),
  };

  sendAnalyticsEvent(analyticsData);
};

const handleMediaGalleryOverlayOpen = ({ index, tab }) => {
  sendViewerOpenEvent(tab);
  if (tab === 'productImages' || tab === 'customerImages') {
    sendMediaImpressionEvent({ index: Number(index), mediaType: ANALYTICS_MEDIA_TYPE[tab] ?? 'NA', locationId: 'Media Gallery' });
  }
};

const handleSlideChange = (index) => {
  debouncedSendMediaImpressionEvent({ index: Number(index), mediaType: 'Product Image', locationId: 'Product Page' });
};

const handleOverlaySlideChange = (payload) => {
  debouncedSendMediaImpressionEvent({ index: Number(payload?.index), mediaType: ANALYTICS_MEDIA_TYPE[payload?.tab] ?? 'NA', locationId: 'Media Gallery' });
};

const handleOverlayTabChange = (payload) => {
  if (payload?.tab === 'productImages' || payload?.tab === 'customerImages') {
    sendMediaImpressionEvent({ index: Number(payload?.index), mediaType: ANALYTICS_MEDIA_TYPE[payload?.tab] ?? 'NA', locationId: 'Media Gallery' });
  }
};

const handleVideoInteraction = ({ interaction, payload }) => {
  if (interaction === 'video_start') {
    sendMediaImpressionEvent({ index: Number(payload?.index), mediaType: ANALYTICS_MEDIA_TYPE.productVideos ?? 'NA', locationId: 'Media Gallery' });
  }
  trackVideoAnalytics({ interaction, payload });
};

onBeforeUnmount(() => {
  debouncedSendMediaImpressionEvent.cancel();
});
</script>

<template>
  <div class="product_media_gallery__images">
    <div
      class="product_media_gallery__images__wrapper"
    >
      <MediaGallery
        v-if="galleryLoaded"
        :product-images="productImages"
        :product-videos="productVideos"
        :mobile-view="isMobileView"
        :customer-images="computedCustomerImages"
        :translations="computedMediaGalleryTranslations"
        @open-overlay="handleMediaGalleryOverlayOpen"
        @slide-change="handleSlideChange"
        @overlay-slide-change="handleOverlaySlideChange"
        @overlay-tab-change="handleOverlayTabChange"
        @video-interaction="handleVideoInteraction"
      />
      <gallery-skeleton-loader
        v-else
      />
    </div>
    <div
      v-if="shouldRenderPromoBanner"
      class="app__render-promo-container"
    >
      <PcRenderPromo
        class="app__render-promo-container__banner"
        :content="props.promoBanner"
        data-test="promo-banner"
      />
    </div>
  </div>
</template>
<style lang="scss">
.product_media_gallery {
  &__images {
    @extend %product-section;
    flex-basis: auto;
    max-width: calc(100% - 1rem);
    width: fit-content;
    margin: 0 1rem 1rem 0;
    padding-left: 1rem;

    @include productBreakpoint(desktop-s) {
      flex-basis: auto;
    }

    @include productBreakpoint('desktop-m') {
      padding-left: 4vw;
      max-width: 50vw;
    }

    @include productBreakpoint('desktop-l') {
      padding-left: 8vw;
    }

    &__wrapper {
      width: 100%;
      margin: 0 auto;

      @include productBreakpoint(max-media-gallery-size) {
        width: fit-content;
        margin: 0 auto;
      }

      @include productBreakpoint(desktop-s) {
        width: fit-content;
        margin: 0;
        margin-left: auto;
      }

      .media-gallery-primary-image__image-container {
        @include productBreakpoint(desktop-s) {
          /* stylelint-disable-next-line */
          width: 25rem !important;
          /* stylelint-disable-next-line */
          height: 25rem !important;
        }
      }
    }

    &__loader {
      width: 11rem;
      margin: 2rem auto;
    }
  }
}
</style>
